import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import type { CoreDepartment, CoreEmployee, CoreLocation, CoreOrganization, EmployeePerformance, UserLabel } from '@/lib/zifekCore';
import { createCoreEmployee, searchUsers, setCoreEmployeeStatus, updateCoreEmployee } from '@/lib/zifekCore';
import { COMMISSION_TYPES, commissionTypeMeta } from '@/lib/coreCatalog';
import Modal from './Modal';
import EmployeePerformancePanel from './EmployeePerformancePanel';

interface Props {
  org: CoreOrganization;
  employees: CoreEmployee[];
  departments: CoreDepartment[];
  locations: CoreLocation[];
  users: Record<number, UserLabel>;
  performance?: Record<number, EmployeePerformance>;
  onChanged: () => void;
}

interface FormState {
  id: number | null;
  userId: number | null;
  targetName: string;
  jobTitle: string;
  departmentId: number | null;
  locationId: number | null;
  managerUserId: number | null;
  email: string;
  phone: string;
  startDate: string;
  endDate: string;
  notes: string;
  commissionType: string;
  commissionValue: string;
  salesTarget: string;
}

const EMPTY: FormState = {
  id: null, userId: null, targetName: '', jobTitle: '', departmentId: null, locationId: null,
  managerUserId: null, email: '', phone: '', startDate: '', endDate: '', notes: '',
  commissionType: 'percentage', commissionValue: '', salesTarget: '',
};

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-foreground-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

export default function EmployeesManager({ org, employees, departments, locations, users, performance, onChanged }: Props) {
  const currency = org.default_currency || 'MAD';
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserLabel[]>([]);
  const [searching, setSearching] = useState(false);

  const memberList = Object.values(users);
  const deptName = (id: number | null) => departments.find((d) => d.id === id)?.name || null;
  const locName = (id: number | null) => locations.find((l) => l.id === id)?.name || null;

  const openCreate = () => {
    setForm(EMPTY);
    setQuery('');
    setResults([]);
    setError('');
    setOpen(true);
  };

  const openEdit = (e: CoreEmployee) => {
    setForm({
      id: e.id,
      userId: e.user_id,
      targetName: e.user_id ? (users[e.user_id]?.name || '') : '',
      jobTitle: e.job_title || '',
      departmentId: e.department_id,
      locationId: e.location_id,
      managerUserId: e.manager_user_id,
      email: e.email || '',
      phone: e.phone || '',
      startDate: e.start_date || '',
      endDate: e.end_date || '',
      notes: e.notes || '',
      commissionType: e.commission_type || 'percentage',
      commissionValue: e.commission_value != null ? String(e.commission_value) : '',
      salesTarget: e.sales_target != null ? String(e.sales_target) : '',
    });
    setQuery('');
    setResults([]);
    setError('');
    setOpen(true);
  };

  const runSearch = useCallback(async (q: string) => {
    setQuery(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      setResults(await searchUsers(q));
    } finally {
      setSearching(false);
    }
  }, []);

  const pickUser = (u: UserLabel) => {
    setForm((f) => ({
      ...f,
      userId: u.id,
      targetName: u.name || u.user_name || '',
      email: f.email || u.email || '',
    }));
    setResults([]);
    setQuery('');
  };

  const clearUser = () => setForm((f) => ({ ...f, userId: null, targetName: '' }));

  const save = async () => {
    if (!form.userId && !form.email.trim() && !form.jobTitle.trim()) {
      setError('Renseignez un compte, un email ou un intitulé de poste.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        tenantId: org.tenant_id,
        organizationId: org.id,
        userId: form.userId,
        jobTitle: form.jobTitle.trim() || null,
        departmentId: form.departmentId,
        locationId: form.locationId,
        managerUserId: form.managerUserId,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        notes: form.notes.trim() || null,
        commissionType: form.commissionType,
        commissionValue: parseFloat(form.commissionValue) || 0,
        salesTarget: parseFloat(form.salesTarget) || 0,
      };
      if (form.id) await updateCoreEmployee(form.id, payload);
      else await createCoreEmployee(payload);
      setOpen(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (e: CoreEmployee) => {
    setBusyId(e.id);
    try {
      await setCoreEmployeeStatus(e.id, e.status === 'inactive' ? 'active' : 'inactive');
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  const label = (e: CoreEmployee) =>
    (e.user_id && users[e.user_id]?.name) || e.email || `Employé #${e.id}`;

  const initials = (e: CoreEmployee) => (label(e).charAt(0) || '?').toUpperCase();

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-foreground-500">
          L’équipe employée de <strong className="text-foreground-700">{org.commercial_name || org.legal_name}</strong> — reliée à un compte Zifek lorsqu’il existe.
        </p>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-user-add-line"></i>
          Nouvel employé
        </button>
      </div>

      <EmployeePerformancePanel employees={employees} performance={performance} users={users} currency={currency} />

      {employees.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-team-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucun employé enregistré.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {employees.map((e) => {
            const inactive = e.status === 'inactive';
            const dept = deptName(e.department_id);
            const loc = locName(e.location_id);
            const ct = commissionTypeMeta(e.commission_type);
            const hasCommission = Number(e.commission_value ?? 0) > 0;
            const perf = performance?.[e.id];
            return (
              <div key={e.id} className={`bg-background-50 border border-background-200/70 rounded-lg p-5 flex flex-col ${inactive ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {initials(e)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground-950 truncate">{label(e)}</div>
                      <div className="text-[11px] text-foreground-400 truncate">{e.job_title || 'Poste non défini'}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${inactive ? 'bg-foreground-100 text-foreground-600' : 'bg-accent-100 text-accent-900'}`}>
                    {inactive ? 'Désactivé' : 'Actif'}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 mb-3">
                  {e.department_id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium whitespace-nowrap"><i className="ri-node-tree mr-1"></i>{dept}</span>}
                  {e.location_id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 font-medium whitespace-nowrap"><i className="ri-store-2-line mr-1"></i>{loc}</span>}
                  {e.user_id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 font-medium whitespace-nowrap"><i className="ri-link-m mr-1"></i>Compte Zifek</span>}
                </div>

                <div className="text-xs text-foreground-500 space-y-1 mb-3">
                  <div className="flex items-center gap-1.5"><i className="ri-mail-line text-foreground-400"></i><span className="truncate">{e.email || '—'}</span></div>
                  <div className="flex items-center gap-1.5"><i className="ri-phone-line text-foreground-400"></i><span className="truncate">{e.phone || '—'}</span></div>
                </div>

                <div className="rounded-lg bg-background-100/70 p-3 mb-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-[11px] text-foreground-600">
                      <i className={`${ct.icon} text-foreground-400`}></i>
                      {hasCommission ? (ct.key === 'fixed' ? `${e.commission_value} ${currency} / vente` : `${e.commission_value} % du montant`) : 'Aucune commission définie'}
                    </span>
                  </div>
                  {perf && perf.orders > 0 && (
                    <div className="mt-2 pt-2 border-t border-background-200/60 flex items-center gap-3 text-[11px]">
                      <span className="flex items-center gap-1 text-foreground-500"><i className="ri-shopping-bag-3-line"></i>{perf.orders} vente{perf.orders > 1 ? 's' : ''}</span>
                      <span className="flex items-center gap-1 text-primary-700 font-semibold"><i className="ri-hand-coin-line"></i>{perf.commission.toLocaleString()} {currency}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-background-200/60 mt-auto">
                  <button
                    onClick={() => openEdit(e)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line"></i> Modifier
                  </button>
                  <button
                    onClick={() => toggleStatus(e)}
                    disabled={busyId === e.id}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer disabled:opacity-50 ${inactive ? 'bg-accent-100 text-accent-900' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}
                  >
                    <i className={inactive ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                    {inactive ? 'Réactiver' : 'Désactiver'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier l’employé' : 'Nouvel employé'}
          onClose={() => setOpen(false)}
          maxWidth="max-w-2xl"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Enregistrer
              </button>
            </>
          }
        >
          {error && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
          <div className="space-y-3">
            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Compte Zifek (facultatif)</span>
              {form.userId ? (
                <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-background-200/70 bg-primary-50">
                  <span className="flex items-center gap-2 text-sm text-foreground-800 min-w-0">
                    <i className="ri-user-3-line text-primary-600"></i>
                    <span className="truncate">{form.targetName || `#${form.userId}`}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 font-medium">Lié</span>
                  </span>
                  <button onClick={clearUser} className="text-xs text-foreground-500 hover:text-foreground-800 underline cursor-pointer whitespace-nowrap">Retirer</button>
                </div>
              ) : (
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
                  <input
                    className={`${INPUT} pl-9`}
                    value={query}
                    onChange={(ev) => runSearch(ev.target.value)}
                    placeholder="Rechercher un compte (nom, pseudo, email)…"
                  />
                </div>
              )}
              {searching && <div className="flex items-center justify-center py-3"><i className="ri-loader-4-line animate-spin text-primary-500"></i></div>}
              {!searching && results.length > 0 && (
                <div className="mt-2 border border-background-200/70 rounded-md divide-y divide-background-200/60 max-h-44 overflow-y-auto">
                  {results.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => pickUser(u)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-xs font-bold text-foreground-600 flex-shrink-0">
                        {(u.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground-900 truncate">{u.name || `#${u.id}`}</div>
                        <div className="text-xs text-foreground-400 truncate">{u.user_name ? `@${u.user_name}` : ''}{u.email ? ` · ${u.email}` : ''}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Field label="Intitulé du poste">
              <input className={INPUT} value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} placeholder="Ex : Responsable commercial" />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Département">
                <select className={INPUT} value={form.departmentId ?? ''} onChange={(e) => setForm({ ...form, departmentId: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">Aucun</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </Field>
              <Field label="Établissement">
                <select className={INPUT} value={form.locationId ?? ''} onChange={(e) => setForm({ ...form, locationId: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">Aucun</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Responsable / Manager">
              <select className={INPUT} value={form.managerUserId ?? ''} onChange={(e) => setForm({ ...form, managerUserId: e.target.value ? Number(e.target.value) : null })}>
                <option value="">Non défini</option>
                {memberList.map((u) => <option key={u.id} value={u.id}>{u.name || u.user_name || `#${u.id}`}</option>)}
              </select>
            </Field>

            <div className="rounded-lg border border-background-200/70 p-3 bg-background-100/50">
              <span className="block text-xs font-medium text-foreground-600 mb-2">Commission sur les ventes</span>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {COMMISSION_TYPES.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setForm({ ...form, commissionType: c.key })}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-colors cursor-pointer ${form.commissionType === c.key ? 'border-primary-400 bg-primary-50/60 text-primary-700' : 'border-background-200/70 text-foreground-600 hover:bg-background-100'}`}
                  >
                    <i className={c.icon}></i>
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={INPUT}
                  value={form.commissionValue}
                  onChange={(e) => setForm({ ...form, commissionValue: e.target.value })}
                  placeholder="0"
                />
                <span className="text-sm font-semibold text-foreground-600 whitespace-nowrap w-16 text-center">
                  {form.commissionType === 'fixed' ? currency : '%'}
                </span>
              </div>
              <p className="text-[11px] text-foreground-400 mt-1.5">Utilisée lorsque la vente est attribuée à cet employé depuis la page Commandes.</p>
            </div>

            <div className="rounded-lg border border-background-200/70 p-3 bg-background-100/50">
              <span className="block text-xs font-medium text-foreground-600 mb-1.5">Objectif de vente</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={INPUT}
                  value={form.salesTarget}
                  onChange={(e) => setForm({ ...form, salesTarget: e.target.value })}
                  placeholder="0"
                />
                <span className="text-sm font-semibold text-foreground-600 whitespace-nowrap w-16 text-center">{currency}</span>
              </div>
              <p className="text-[11px] text-foreground-400 mt-1.5">Alimente le suivi de performance (top vendeurs & progression) dans cet onglet.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Email"><input className={INPUT} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Téléphone"><input className={INPUT} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Date d’entrée"><input type="date" className={INPUT} value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></Field>
              <Field label="Date de sortie"><input type="date" className={INPUT} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></Field>
            </div>

            <Field label="Notes">
              <textarea className={`${INPUT} min-h-[70px]`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}